import React from 'react';
import SEO from '../components/SEO';
import TransitionLink from '../components/TransitionLink';
import LightHeader from '../components/LightHeader';
import LightFooter from '../components/LightFooter';
import { useLanguage } from '../context/LanguageContext';

const content = {
    en: {
        tag: 'Full-Stack Development',
        h1a: 'Full-Stack',
        h1b: 'Developer',
        bio: 'I came from fifteen years of broadcast production, where code either works live or it does not work at all. That background shapes how I build: React, Python, Supabase, tools for real workflows, shipped under pressure.',
        getInTouch: 'Get in touch',
        pillars: [
            {
                title: 'Production discipline',
                body: 'Fifteen years shipping graphics live on national television. No staging environment. No second chance. That mindset carries directly into how I write and deploy code.',
            },
            {
                title: 'Full-stack, not half-stack',
                body: 'React on the front, Supabase and PostgreSQL on the back, Vercel for deployment. I own the whole pipeline, from data model to UI.',
            },
            {
                title: 'Tools that solve real problems',
                body: 'Blender addons, automation scripts, Discord bots for AI image generation. I build what does not exist yet, not what is already on npm.',
            },
        ],
        selectedProjects: 'Selected Projects',
        viewProject: 'View project',
        stackTitle: 'Stack',
        stackGroups: [
            { label: 'Frontend', items: ['React', 'Tailwind CSS', 'Three.js / R3F', 'PWA'] },
            { label: 'Backend', items: ['Supabase', 'PostgreSQL', 'Vercel Serverless', 'Python'] },
            { label: 'Tools and scripting', items: ['Python', 'Blender API', 'ExtendScript', 'Discord.py'] },
        ],
        ctaText: "Tools built for real workflows. If you need someone who ships under pressure, let's talk.",
        fullPortfolio: 'Full portfolio',
        projects: [
            {
                id: 'lifeos',
                title: 'LifeOS',
                category: 'React Web App',
                year: '2025',
                description: 'Personal productivity platform integrating Strava, Apple Health, and Telegram. React, Supabase, PostgreSQL, Vercel serverless functions.',
                image: '/img/code/lifeos_hero.png',
                link: '/code/lifeos',
                tags: ['React', 'Supabase', 'PostgreSQL', 'Vercel'],
            },
            {
                id: 'blender-studio-tools',
                title: 'Blender Studio Tools',
                category: 'Blender Addon / Python',
                year: '2023',
                description: 'Production-grade Blender addon for broadcast studio workflows. Viewport-to-camera matrix conversion, constraint systems, custom UI panels. Open source.',
                image: '/img/code/blender-main.png',
                link: '/code/blender-studio-tools',
                tags: ['Python', 'Blender API', 'Open Source'],
            },
            {
                id: 'trellis-3d-generator',
                title: 'Trellis 3D Blender Addon',
                category: 'Blender Addon / AI',
                year: '2025',
                description: 'Blender addon that connects to Microsoft Trellis to generate 3D assets from images directly inside the viewport. GPU monitoring, async pipeline, Gradio client.',
                image: '/img/code/trellis-main.png',
                link: '/code/trellis-3d-generator',
                tags: ['Python', 'AI', 'Gradio', 'Blender API'],
            },
        ],
    },
    it: {
        tag: 'Sviluppo Full-Stack',
        h1a: 'Sviluppatore',
        h1b: 'Full-Stack',
        bio: 'Vengo da quindici anni di produzione broadcast, dove il codice funziona in diretta o non funziona del tutto. Quel background definisce come costruisco: React, Python, Supabase, strumenti per workflow reali, consegnati sotto pressione.',
        getInTouch: 'Contattami',
        pillars: [
            {
                title: 'Disciplina produttiva',
                body: 'Quindici anni a spedire grafica in diretta sulla televisione nazionale. Nessun ambiente di staging. Nessuna seconda possibilita. Quella mentalita si trasferisce direttamente nel modo in cui scrivo e deploya codice.',
            },
            {
                title: 'Full-stack, non meta-stack',
                body: 'React sul front, Supabase e PostgreSQL sul back, Vercel per il deployment. Gestisco tutta la pipeline, dal modello dati alla UI.',
            },
            {
                title: 'Strumenti che risolvono problemi reali',
                body: 'Addon per Blender, script di automazione, bot Discord per la generazione di immagini AI. Costruisco cio che non esiste ancora, non quello che e gia su npm.',
            },
        ],
        selectedProjects: 'Progetti Selezionati',
        viewProject: 'Vedi progetto',
        stackTitle: 'Stack',
        stackGroups: [
            { label: 'Frontend', items: ['React', 'Tailwind CSS', 'Three.js / R3F', 'PWA'] },
            { label: 'Backend', items: ['Supabase', 'PostgreSQL', 'Vercel Serverless', 'Python'] },
            { label: 'Strumenti e scripting', items: ['Python', 'Blender API', 'ExtendScript', 'Discord.py'] },
        ],
        ctaText: 'Strumenti costruiti per workflow reali. Se cerchi qualcuno che consegna sotto pressione, parliamone.',
        fullPortfolio: 'Portfolio completo',
        projects: [
            {
                id: 'lifeos',
                title: 'LifeOS',
                category: 'React Web App',
                year: '2025',
                description: 'Piattaforma di produttivita personale che integra Strava, Apple Health e Telegram. React, Supabase, PostgreSQL, Vercel serverless functions.',
                image: '/img/code/lifeos_hero.png',
                link: '/code/lifeos',
                tags: ['React', 'Supabase', 'PostgreSQL', 'Vercel'],
            },
            {
                id: 'blender-studio-tools',
                title: 'Blender Studio Tools',
                category: 'Blender Addon / Python',
                year: '2023',
                description: 'Addon per Blender a livello produttivo per workflow di studio broadcast. Conversione matrice viewport-camera, sistemi di constraint, pannelli UI custom. Open source.',
                image: '/img/code/blender-main.png',
                link: '/code/blender-studio-tools',
                tags: ['Python', 'Blender API', 'Open Source'],
            },
            {
                id: 'trellis-3d-generator',
                title: 'Trellis 3D Blender Addon',
                category: 'Blender Addon / AI',
                year: '2025',
                description: 'Addon per Blender che si connette a Microsoft Trellis per generare asset 3D da immagini direttamente nel viewport. Monitoraggio GPU, pipeline asincrona, client Gradio.',
                image: '/img/code/trellis-main.png',
                link: '/code/trellis-3d-generator',
                tags: ['Python', 'AI', 'Gradio', 'Blender API'],
            },
        ],
    },
};

const LandingDev = () => {
    const { language } = useLanguage();
    const t = content[language];

    return (
        <>
            <SEO
                title={language === 'it' ? 'Sviluppatore Full-Stack' : 'Full-Stack Developer'}
                description={language === 'it'
                    ? 'Sviluppatore full-stack con quindici anni di background nella produzione broadcast. React, Python, Supabase. Strumenti per workflow reali, consegnati sotto pressione.'
                    : 'Full-stack developer with fifteen years of broadcast production background. React, Python, Supabase. Tools built for real workflows, shipped under pressure.'}
                url="/landing-dev"
                language={language}
            />

            <div className="min-h-screen bg-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>

                <LightHeader ctaLabel={t.getInTouch} />

                {/* Hero */}
                <section className="pt-40 pb-24 px-8 md:px-16 max-w-6xl mx-auto">
                    <p className="text-orange-400 text-sm font-mono tracking-widest uppercase mb-6">
                        {t.tag}
                    </p>
                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tight leading-none mb-10">
                        {t.h1a}<br />
                        <span className="text-orange-400">{t.h1b}</span>
                    </h1>
                    <p className="text-xl md:text-2xl text-white/70 font-light leading-relaxed max-w-2xl mb-12">
                        {t.bio}
                    </p>
                    <a
                        href="mailto:info@paoloallegretti.com"
                        className="inline-flex items-center text-white font-medium text-lg group"
                    >
                        <span className="border-b border-white/30 group-hover:border-orange-400 group-hover:text-orange-400 transition-colors duration-300 pb-0.5">
                            info@paoloallegretti.com
                        </span>
                        <span className="ml-2 group-hover:translate-x-1 transition-transform duration-300">→</span>
                    </a>
                </section>

                {/* Pillars */}
                <section className="border-t border-b border-white/10 py-16 px-8 md:px-16">
                    <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
                        {t.pillars.map((item) => (
                            <div key={item.title} className="border border-white/10 rounded-2xl p-8 hover:border-orange-400/30 transition-colors duration-300">
                                <h3 className="text-lg font-black text-white mb-4 tracking-tight">{item.title}</h3>
                                <p className="text-white/50 leading-relaxed text-sm">{item.body}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Projects */}
                <section className="py-24 px-8 md:px-16 max-w-6xl mx-auto">
                    <h2 className="text-sm font-mono text-white/40 tracking-widest uppercase mb-16">{t.selectedProjects}</h2>
                    <div className="space-y-6">
                        {t.projects.map((project) => (
                            <TransitionLink
                                key={project.id}
                                to={project.link}
                                className="group block border border-white/10 rounded-2xl overflow-hidden hover:border-orange-400/40 transition-colors duration-300"
                            >
                                <div className="flex flex-col md:flex-row">
                                    <div className="w-full md:w-64 h-48 md:h-auto bg-white/5 flex-shrink-0 overflow-hidden">
                                        <img
                                            src={project.image}
                                            alt={project.title}
                                            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                                        />
                                    </div>
                                    <div className="p-8 flex flex-col justify-between flex-1">
                                        <div>
                                            <div className="flex items-center gap-4 mb-4">
                                                <span className="text-xs font-mono text-orange-400 tracking-widest uppercase">{project.category}</span>
                                                <span className="text-xs font-mono text-white/30">{project.year}</span>
                                            </div>
                                            <h3 className="text-2xl md:text-3xl font-black text-white mb-3 tracking-tight group-hover:text-orange-400 transition-colors duration-300">
                                                {project.title}
                                            </h3>
                                            <p className="text-white/50 leading-relaxed mb-4">{project.description}</p>
                                            <div className="flex flex-wrap gap-2">
                                                {project.tags.map((tag) => (
                                                    <span key={tag} className="text-xs font-mono text-white/30 border border-white/10 px-2 py-1 rounded">
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="mt-6">
                                            <span className="text-sm text-white/30 font-mono group-hover:text-orange-400 transition-colors duration-300">
                                                {t.viewProject} →
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </TransitionLink>
                        ))}
                    </div>
                </section>

                {/* Stack */}
                <section className="py-16 px-8 md:px-16 border-t border-white/10">
                    <div className="max-w-6xl mx-auto">
                        <h2 className="text-sm font-mono text-white/40 tracking-widest uppercase mb-10">{t.stackTitle}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {t.stackGroups.map((group) => (
                                <div key={group.label}>
                                    <p className="text-xs font-mono text-orange-400 tracking-widest uppercase mb-4">{group.label}</p>
                                    <ul className="space-y-2">
                                        {group.items.map((item) => (
                                            <li key={item} className="text-white/60 text-sm font-mono">{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Contact */}
                <section className="py-24 px-8 md:px-16">
                    <div className="max-w-6xl mx-auto">
                        <p className="text-3xl md:text-5xl font-black text-white tracking-tight leading-tight mb-10 max-w-2xl">
                            {t.ctaText}
                        </p>
                        <a
                            href="mailto:info@paoloallegretti.com"
                            className="text-3xl md:text-5xl font-black text-white/30 hover:text-orange-400 transition-colors duration-300 tracking-tight"
                        >
                            info@paoloallegretti.com
                        </a>
                        <LightFooter fullPortfolioLabel={t.fullPortfolio} />
                    </div>
                </section>

            </div>
        </>
    );
};

export default LandingDev;
